/**
 * Display + ordering helpers for sales-position dropdowns (Sales Hierarchy,
 * rep pickers). Pure functions so the two tester complaints are unit-tested:
 *  - "Not all names show / a literal blank entry appears" → displaySalesPositionName
 *    never returns an empty string (falls back to title, then a placeholder).
 *  - "Order the dropdown alphabetically by first name" → sortSalesPositionsByFirstName.
 */

/**
 * Sales-hierarchy tier → `sales_positions.position_type` values. SINGLE SOURCE
 * OF TRUTH shared by the event-create Sales Hierarchy pickers (EventForm) and
 * the events-list rep/manager filters (EventsRepFilter / Events page) so the two
 * never drift — a rep assignable on a tier the filter can't select would make
 * those events un-findable. (Note the historical 'sales_director' vs real
 * 'director' value bug; keep these aligned with the live schema.)
 */
export const LEAD_EVENT_REP_POSITION_TYPES = [
  "sales_rep",
  "territory_manager",
  "regional_manager",
  "director",
];
export const TERRITORY_MANAGER_POSITION_TYPES = ["territory_manager"];
export const REGIONAL_MANAGER_POSITION_TYPES = ["regional_manager"];
export const SALES_DIRECTOR_POSITION_TYPES = ["director"];

export interface SalesPositionNameFields {
  assigned_name?: string | null;
  title?: string | null;
  is_active?: boolean;
}

/**
 * Human label for a sales position: assigned name, else title, else a visible
 * placeholder — NEVER an empty string (which rendered as a blank/odd dropdown
 * row). Optionally appends "(inactive)".
 */
export function displaySalesPositionName(
  sp: SalesPositionNameFields,
  opts?: { markInactive?: boolean },
): string {
  const base =
    sp.assigned_name?.trim() || sp.title?.trim() || "(Unnamed position)";
  if (opts?.markInactive && sp.is_active === false) {
    return `${base} (inactive)`;
  }
  return base;
}

/** First-name token (before the first whitespace) of the display name, lowercased. */
export function firstNameOf(sp: SalesPositionNameFields): string {
  const name = sp.assigned_name?.trim() || sp.title?.trim() || "";
  return name.split(/\s+/)[0]?.toLowerCase() ?? "";
}

/**
 * Return a NEW array sorted alphabetically by first name (case-insensitive),
 * tie-broken by full display name for stability. Operators asked for the rep
 * dropdowns to be ordered by first name for quick scanning.
 */
export function sortSalesPositionsByFirstName<
  T extends SalesPositionNameFields,
>(list: ReadonlyArray<T>): T[] {
  return [...list].sort((a, b) => {
    const fa = firstNameOf(a);
    const fb = firstNameOf(b);
    if (fa !== fb) {
      return fa < fb ? -1 : 1;
    }
    return displaySalesPositionName(a).localeCompare(
      displaySalesPositionName(b),
    );
  });
}
